import { useEffect, useSyncExternalStore } from 'react';
import { getJWTToken } from '../../utils/auth';
import { currentSchoolYear } from '../../core/schoolYearsApi';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';
const ROLLOVER_CHECK_MS = 60_000;

export type PlansStatus = {
  generated: boolean;
  dataVersion: number;
  generatedAt: string | null;
  stale: boolean;
  schoolYear?: string;
  unknown?: boolean;
};

type StatusBody = {
  generated?: boolean;
  dataVersion?: number;
  generatedAt?: string;
  stale?: boolean;
  schoolYear?: string;
};

let cache: PlansStatus | null = null;
let cachedAtYear: string | null = null;
let rolloverTimer: ReturnType<typeof setInterval> | null = null;
let inFlight: Promise<PlansStatus> | null = null;
let markingEpoch: number | null = null;
let refreshInFlight: Promise<void> | null = null;
let epoch = 0;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function setCache(next: PlansStatus | null, atYear?: string | null): void {
  cache = next;
  cachedAtYear = next === null ? null : (atYear ?? currentSchoolYear());
  notify();
}

function checkRollover(): void {
  if (cachedAtYear === null || cachedAtYear === currentSchoolYear()) return;
  revalidate();
}

function revalidate(): void {
  const startedEpoch = epoch;
  fetchPlansStatus().catch(() => {
    if (epoch !== startedEpoch) return;
    if (cachedPlansStatus() === null) setCache({ ...normalize({}), unknown: true });
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (rolloverTimer === null) rolloverTimer = setInterval(checkRollover, ROLLOVER_CHECK_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && rolloverTimer !== null) {
      clearInterval(rolloverTimer);
      rolloverTimer = null;
    }
  };
}

function normalize(body: StatusBody): PlansStatus {
  const status: PlansStatus = {
    generated: body.generated === true,
    dataVersion: body.dataVersion ?? 0,
    generatedAt: body.generatedAt || null,
    stale: body.stale === true,
  };
  if (body.schoolYear) status.schoolYear = body.schoolYear;
  return status;
}

export function cachedPlansStatus(): PlansStatus | null {
  return cache;
}

export function clearPlansStatusCache(): void {
  epoch += 1;
  inFlight = null;
  refreshInFlight = null;
  setCache(null);
}

async function requestStatus(): Promise<PlansStatus> {
  const startedEpoch = epoch;
  const requestedYear = currentSchoolYear();
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/plans/status?schoolYear=${encodeURIComponent(requestedYear)}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`plans-status failed: ${response.status}`);
  }
  const status = normalize((await response.json()) as StatusBody);
  if (markingEpoch !== epoch && epoch === startedEpoch) {
    setCache(status, requestedYear);
  }
  return status;
}

export function fetchPlansStatus(): Promise<PlansStatus> {
  if (!inFlight) {
    const request: Promise<PlansStatus> = requestStatus().finally(() => {
      if (inFlight === request) inFlight = null;
    });
    inFlight = request;
  }
  return inFlight;
}

const noSubscription = (): (() => void) => () => {};
const noStatus = (): PlansStatus | null => null;

export function usePlansStatus(enabled = true): PlansStatus | null {
  const status = useSyncExternalStore(enabled ? subscribe : noSubscription, enabled ? cachedPlansStatus : noStatus);
  useEffect(() => {
    if (enabled) revalidate();
  }, [enabled]);
  useEffect(() => {
    if (enabled && status === null && cache !== null) revalidate();
  }, [enabled, status]);
  return status;
}

export async function markPlansGenerated(): Promise<PlansStatus> {
  const previous = cache;
  const previousYear = cachedAtYear;
  const requestedYear = currentSchoolYear();
  epoch += 1;
  const markEpoch = epoch;
  markingEpoch = markEpoch;
  inFlight = null;
  setCache({
    generated: true,
    dataVersion: previous?.dataVersion ?? 0,
    generatedAt: new Date().toISOString(),
    stale: false,
    ...(previous?.schoolYear ? { schoolYear: previous.schoolYear } : {}),
  });
  try {
    const token = await getJWTToken();
    if (epoch !== markEpoch) {
      throw new Error('mark-plans-generated superseded');
    }
    const response = await fetch(`${apiUrl}/plans/generated`, {
      method: 'POST',
      headers: {
        ...authHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ schoolYear: requestedYear }),
    });
    if (!response.ok) {
      throw new Error(`mark-plans-generated failed: ${response.status}`);
    }
    const status = normalize((await response.json()) as StatusBody);
    if (epoch === markEpoch) {
      epoch += 1;
      setCache(status, requestedYear);
    }
    return status;
  } catch (err) {
    if (epoch === markEpoch) {
      epoch += 1;
      setCache(previous, previousYear);
      inFlight = null;
      revalidate();
    }
    throw err;
  } finally {
    if (markingEpoch === markEpoch) markingEpoch = null;
  }
}

export function refreshPlansGeneratedMarker(): Promise<void> {
  if (!refreshInFlight) {
    const request: Promise<void> = runMarkerRefresh().finally(() => {
      if (refreshInFlight === request) refreshInFlight = null;
    });
    refreshInFlight = request;
  }
  return refreshInFlight;
}

async function runMarkerRefresh(): Promise<void> {
  const startedEpoch = epoch;
  const status = await fetchPlansStatus();
  if (epoch !== startedEpoch) return;
  if (status.generated) await markPlansGenerated();
}

export function formatLastUpdated(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    timeZone: 'UTC',
  });
}
